import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddRubroMutation } from "@/features/auxiliar/rubros/api/add-rubro";
import { DeleteRubroMutation } from "@/features/auxiliar/rubros/api/delete-rubro";
import { EditRubroMutation } from "@/features/auxiliar/rubros/api/edit-rubro";
import { getAllRubros, useGetAllRubrosQuery } from "@/features/auxiliar/rubros/api/get-all-rubros";
import { GetRubroQuery } from "@/features/auxiliar/rubros/api/get-rubro";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import RubroForm, { rubroFormSchema } from "@/features/auxiliar/rubros/components/rubro-form";

export default function RubrosPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteRubros);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllRubrosQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => row.nombre },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "rubros"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Rubros">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Rubro</ModalHeader>
            <AddRubroMutation>
                {mutation => <SchemaFormHook
                    schema={rubroFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("El rubro se ha agregado correctamente.");
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <RubroForm form={form} flex={1} padding={3} />}
                </SchemaFormHook>}
            </AddRubroMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Rubro</ModalHeader>
            <ModalValueExtractor>
                {rubroId => <GetRubroQuery id={rubroId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {rubro => <EditRubroMutation id={rubro.id}>
                            {mutation => <SchemaFormHook
                                schema={rubroFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("El rubro se ha editado correctamente.");
                                }}
                                defaultValues={{ ...rubro }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <RubroForm form={form} flex={1} padding={3} />}
                            </SchemaFormHook>}
                        </EditRubroMutation>}
                    </DataFetcher>}
                </GetRubroQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {rubro => <DeleteRubroMutation id={rubro.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Rubro "${rubro.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el rubro "${rubro.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El rubro "${rubro.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteRubroMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllRubros}
            defaultFileTitle="Rubros"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllRubros}
            defaultFileTitle="Rubros"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllRubros}
            defaultFileTitle="Rubros"
        />
    </ListPage>
}